// apps/web/src/features/russia-requests/components/tabs/RussiaPositionCard.tsx
//
// Read-only card for one SIMPLE Russia position. Displays all fields
// including description/brand/productQuantity that the old flat table omitted.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RussiaRequestItem } from '@magimp/shared';

import { RussiaItemPhotoPreviews } from './RussiaItemPhotoPreviews.js';

function PositionField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <dt style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 500 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 13, color: 'var(--fg-1)', wordBreak: 'break-word' }}>
        {children}
      </dd>
    </div>
  );
}

export interface RussiaPositionCardProps {
  requestId: string;
  item: RussiaRequestItem;
}

export function RussiaPositionCard({ requestId, item }: RussiaPositionCardProps) {
  const { t } = useTranslation('russiaRequests');
  return (
    <article
      data-testid={`russia-position-card-${item.id}`}
      aria-label={item.name}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: 14,
          color: 'var(--fg-1)',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {item.name}
      </div>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px 16px',
        }}
      >
        {/* WHY: description spans full width so long text doesn't break
            the 2-col grid into an unreadable narrow column. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <PositionField label={t('drawer.items.columns.description')}>
            {item.description || '—'}
          </PositionField>
        </div>
        <PositionField label={t('drawer.items.columns.weightKg')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {item.weightKg === null ? '—' : String(item.weightKg)}
          </span>
        </PositionField>
        <PositionField label={t('drawer.items.columns.volumeM3')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {item.volumeM3 === null ? '—' : String(item.volumeM3)}
          </span>
        </PositionField>
        <PositionField label={t('drawer.items.columns.quantity')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{item.quantity}</span>
        </PositionField>
        <PositionField label={t('drawer.items.columns.brand')}>{item.brand || '—'}</PositionField>
        <PositionField label={t('drawer.items.columns.productQuantity')}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{item.productQuantity ?? '—'}</span>
        </PositionField>
        <PositionField label={t('drawer.items.columns.material')}>
          {item.material ?? '—'}
        </PositionField>
        <div style={{ gridColumn: '1 / -1' }}>
          <PositionField label={t('drawer.items.columns.photos')}>
            <RussiaItemPhotoPreviews requestId={requestId} itemId={item.id} photos={item.photos} />
          </PositionField>
        </div>
      </dl>
    </article>
  );
}
